import { startOfToday, subYears } from 'date-fns';
import httpClient from '@common/api/httpClient';
import paramsSerializer from '@common/api/paramsSerializer';
import { type LogViewingRights } from '@common/hooks/useLogViewingRights';
import {
  type LogType,
  type PaginatedLogsResponse,
  type Subscriber,
  type SubscriberCreate,
  type SubscriberUpdate,
} from '@common/types/api';
import { type InstallationId, type TimestampUTC } from '@common/types/brandedTypes';
import type DeviceLogEvent from '@common/types/models/DeviceLogEvent';
import {
  createDeviceLogEventFromResponse,
  LogEventSeverity,
  LogLevel,
} from '@common/types/models/DeviceLogEvent';
import { formatAsUtcTimestamp } from '@common/utils/datetime/isoTimestamp';
import { type ListWithCount } from '@common/utils/formatting/paginationQueryHelpers';

export async function fetchNotificationContacts(
  installationId: InstallationId,
  signal: AbortSignal,
): Promise<Subscriber[]> {
  try {
    const { data } = await httpClient.get<Subscriber[]>(
      `/rest/v1/UserNotificationRegistrations/${installationId}`,
      { signal },
    );
    return data;
  } catch {
    return [];
  }
}

export async function fetchLogTypes(
  logViewingRights: LogViewingRights,
  signal: AbortSignal,
) {
  try {
    const { data } = await httpClient.get<LogType[]>('rest/v1/Logs/Types', { signal });
    // Only exclude those types that have specifically been marked with false
    return data.filter((type) => logViewingRights[type.id] !== false);
  } catch {
    return [];
  }
}

interface FetchDeviceLogsOptions {
  minLogLevel?: LogEventSeverity;
  activeAlarm?: boolean;
  type?: number;
  signal?: AbortSignal;
}

export async function fetchDeviceLogs(
  installationId: InstallationId,
  skip: number,
  take: number,
  logViewingRights: LogViewingRights,
  options: FetchDeviceLogsOptions = {},
): Promise<ListWithCount<DeviceLogEvent>> {
  const {
    minLogLevel = LogEventSeverity.All,
    activeAlarm,
    type,
    signal,
  } = options;
  try {
    const periodStart: TimestampUTC = formatAsUtcTimestamp(subYears(startOfToday(), 2));
    let severities;
    if (minLogLevel !== LogEventSeverity.All) {
      severities = Object.values(LogLevel)
        .filter((severity: number) => severity >= LogLevel[minLogLevel].valueOf());
    }
    // Only exclude those types that have specifically been marked with false
    const excludeTypes = Object.entries(logViewingRights)
      .filter(([, value]) => value === false)
      .map(([key]) => Number(key));

    const {
      data: { count, logs = [] },
    } = await httpClient.get<PaginatedLogsResponse>(
      `/rest/v1/Logs/installation/${installationId}`,
      {
        params: {
          periodStart,
          skip,
          take,
          severities,
          types: type ? [type] : undefined,
          excludeTypes,
          activeAlarm,
        },
        paramsSerializer,
        signal,
      },
    );
    return {
      count,
      items: logs.map(createDeviceLogEventFromResponse),
    };
  } catch {
    return {
      count: 0,
      items: [],
    };
  }
}

export function deactivateAlarm(
  installationId: InstallationId,
  alarmId: number,
): Promise<undefined> {
  return httpClient.put(
    `/rest/v1/AlarmDeactivation/installation/${installationId}/installationEvent/${alarmId}/deactivate`,
  );
}

export async function deactivateAllAlarms(
  installationId: InstallationId,
  logViewingRights: LogViewingRights,
): Promise<void> {
  const limit = 5;
  const {
    items: alarms,
    count,
  } = await fetchDeviceLogs(
    installationId,
    0,
    limit,
    logViewingRights,
    {
      minLogLevel: LogEventSeverity.Error,
      activeAlarm: true,
    },
  );
  if (count > 0) {
    await Promise.all(alarms.map((alarm) => deactivateAlarm(installationId, alarm.id)));
  }
  if (count > limit) {
    await deactivateAllAlarms(installationId, logViewingRights);
  }
}

export function deleteNotificationContact(subscriberId: number) {
  return httpClient.delete(`/rest/v1/UserNotificationRegistrations/${subscriberId}`);
}

export function updateNotificationContact(
  id: number,
  installationId: InstallationId,
  subscriber: SubscriberUpdate,
) {
  return httpClient.put(
    `/rest/v1/UserNotificationRegistrations/${id}`,
    {
      ...subscriber,
      installationId,
    },
  );
}

export function createNotificationContact(
  subscriber: SubscriberCreate,
  installationId: InstallationId,
) {
  return httpClient.post<Subscriber>(
    '/rest/v1/UserNotificationRegistrations',
    {
      ...subscriber,
      installationId,
    },
  );
}
